import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { CountryCodesEnum, frenchTerritoriesOptions } from '../../../../resources/base/models/country';
import { CustomerDetailsSection } from '../../../../resources/base/models/enums';
import {
  CitiesAndZipCodesSuggestion,
  localitiesWithZipCodes,
  zipCodesForLocality,
} from '../../../../services/api/address/localitiesAndZipCodes';
import {
  createStakeholderContact,
  removeStakeholderContact,
  updateStakeholderContact,
} from '../../../../services/api/miles/miles-customer-services';
import { unlinkStakeholderContact } from '../../../../services/api/miles/miles-services';
import { updateDataStorageNew } from '../../../../services/api/updateDataStorage/updateDataStorage';
import { getTransactionId } from '../../../../services/common/data';
import { assertDefined } from '../../../../services/common/miscUtils';
import { staticSelector } from '../../../../services/redux';
import {
  creditApplicationSelectors,
  setFormAsNotSubmitted,
} from '../../../../services/redux/features/creditApplication.slice';
import {
  customerSelectors,
  setAvailableSignatories,
  setRemoveStakeholderContact,
  setShouldLinkSignatoryStakeholder,
  setStakeholderContact,
  StakeholderContactDefinition,
} from '../../../../services/redux/features/customer.slice';
import {
  selectCreditApplicationStakeholdersFromDataStorage,
  selectMilesCustomerIdFromDataStorage,
  selectQuoteIdFromDataStorage,
  setDataStorageNew,
} from '../../../../services/redux/features/dataStorage.redux';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { quoteSelectors } from '../../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { validateEmailTls } from '../../utils/checkTLSUtil';
import {
  CeoBirthCountry,
  CeoBirthName,
  CeoBirthPlaceCity,
  CeoBirthPlaceZip,
  CeoDateOfBirth,
  CeoFirstName,
  CeoLastName,
  CeoNationality,
  CountryCodeMobilePhone,
  Email,
  Gender,
  MobilePhone,
} from '../../utils/fieldsBusinessCustomer';
import { formatCityNameLettersAndSpaceOnly } from '../../utils/formatCityString';
import { CustomerDetailsFormGroupForms, CustomerDetailsFormGroupType, useFormSectionHelpers } from '../utils';
import { getStakeholderInfoDataFormInitialValues, SignatoryInfoDataFormType } from './initialValues';
import { signatoryInfoDataFormCreateRequestMapper, signatoryInfoDataFormUpdateRequestMapper } from './requestMapper';
import { SignatorySelectionModal } from './SignatorySelectionModal';
import { StakeholderRemovalModal } from './StakeholderRemovalModal';
import { getSignatoryInfoDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'business'>;
  formGroup: CustomerDetailsFormGroupType;
}

const pristineStakeholderValues = getStakeholderInfoDataFormInitialValues();

export const SignatoryInfoDataForm: FunctionComponent<Props> = ({ formName, formGroup }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const milesCustomerId =
    useSelector(customerSelectors.selectCustomerId) || staticSelector(selectMilesCustomerIdFromDataStorage);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<SignatoryInfoDataFormType> | null>(null);
  const isSignatoryFormActive = useSelector(customerSelectors.selectIsSignatoryFormActive);
  const stakeholderContacts = useSelector(customerSelectors.selectStakeholderContacts);
  const creditApplicationStakeholders = staticSelector(selectCreditApplicationStakeholdersFromDataStorage);
  const signatoryContactData = useMemo(() => stakeholderContacts?.SIGNATORY, [stakeholderContacts]);
  const availableSignatories = useSelector(customerSelectors.selectAvailableSignatories);

  useEffect(() => {
    setValidationSchema(isSignatoryFormActive ? getSignatoryInfoDataFormValidationSchema(t) : null);
  }, [isSignatoryFormActive, t]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    formGroup,
    formName
  );

  const onSignatoryClear = () => {
    dispatch(setRemoveStakeholderContact(signatoryContactData!));
  };

  const onSignatoryRemoval = async (signatoryToRemove) => {
    try {
      dispatch(loadingActions.setRenderingLocalSpinner(true));
      await removeStakeholderContact(
        assertDefined(milesCustomerId),
        assertDefined(signatoryToRemove.milesStakeholderContactId)
      );

      if (signatoryToRemove.milesContactId === signatoryContactData?.milesContactId) {
        dispatch(setRemoveStakeholderContact(signatoryContactData!));
      }
      const clearedSigntories = availableSignatories.filter(
        (availableSignatory) => availableSignatory.milesContactId !== signatoryToRemove.milesContactId
      );
      dispatch(setAvailableSignatories(clearedSigntories));
      dispatch(loadingActions.setRenderingLocalSpinner(false));
    } catch (e) {
      dispatch(loadingActions.setRenderingLocalSpinner(false));
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onSubmit = async (values: SignatoryInfoDataFormType) => {
    if (!isSignatoryFormActive) {
      onFormSubmitSuccess();
      return;
    }

    dispatch(loadingActions.setLoading(true));
    try {
      if (signatoryContactData?.milesContactId) {
        const response = await updateStakeholderContact(
          assertDefined(milesCustomerId),
          assertDefined(signatoryContactData?.milesStakeholderContactId),
          signatoryInfoDataFormUpdateRequestMapper(
            values,
            signatoryContactData.milesMobilePhoneNumberId,
            signatoryContactData.milesEmailId
          )
        );

        const responseContact = response.stakeholderContacts?.at(0);
        if (responseContact) {
          dispatch(
            setStakeholderContact({
              role: 'SIGNATORY',
              milesContactId: assertDefined(responseContact.milesContactId),
              milesStakeholderContactId: assertDefined(signatoryContactData?.milesStakeholderContactId),
              milesEmailId: responseContact.contactData?.emails?.at(0)?.milesEmailId,
              milesMobilePhoneNumberId: responseContact.contactData?.milesMobilePhoneNumberId,
              formData: values,
            })
          );
        }
      } else {
        const response = await createStakeholderContact(
          assertDefined(milesCustomerId),
          signatoryInfoDataFormCreateRequestMapper(values)
        );

        dispatch(setShouldLinkSignatoryStakeholder(true));

        dispatch(
          setStakeholderContact({
            role: 'SIGNATORY',
            milesContactId: assertDefined(response.milesContactId),
            milesStakeholderContactId: assertDefined(
              response.stakeholderContacts!.find((contact) => contact.milesStakeholderContactType === 'SIGNATORY')!
                .milesStakeholderContactId
            ),
            milesEmailId: response.contactData?.emails?.at(0)?.milesEmailId,
            milesMobilePhoneNumberId: response.contactData?.milesMobilePhoneNumberId,
            formData: values,
          })
        );
      }

      if (signatoryContactData?.milesContactId) {
        await updateDataStorageNew(getTransactionId(), {
          custom: {
            creditApplicationStakeholders: {
              ...creditApplicationStakeholders,
              signatoryContactId: signatoryContactData.milesContactId,
            },
          },
        }).then((updatedDataStorageItem) => dispatch(setDataStorageNew(updatedDataStorageItem.custom)));

        if (
          !availableSignatories.some((signatory) => signatory.milesContactId === signatoryContactData.milesContactId)
        ) {
          setAvailableSignatories([...availableSignatories, signatoryContactData]);
        }
      }
    } catch {
      navigate.error();
      return;
    } finally {
      dispatch(loadingActions.setLoading(false));
    }

    onFormSubmitSuccess();
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);
  const signatoryData = useMemo(() => stakeholderContacts.SIGNATORY, [stakeholderContacts]);
  const defaultOpen = isSignatoryFormActive ? formSectionProps.defaultOpen : false;

  // hack - we cannot imperatively set open or closed on form section
  const formSectionKey = isSignatoryFormActive ? 1 : 0;

  return (
    <ContentSection.ComponentWrapper>
      <FormSection
        title={t('step4:heading:signatoryInformation')}
        {...formSectionProps}
        disabled={formSectionProps.disabled || !isSignatoryFormActive}
        defaultOpen={defaultOpen}
        key={formSectionKey}
      >
        <Formik
          enableReinitialize
          initialValues={signatoryData?.formData ?? pristineStakeholderValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent
              onFormInitialValuesChange={onFormInitialValuesChange}
              disableAllFields={!isSignatoryFormActive}
              validationSchema={validationSchema}
              onSignatoryFormDataClear={onSignatoryClear}
              onSignatoryDelete={onSignatoryRemoval}
            />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
  disableAllFields: boolean;
  validationSchema: ObjectSchema<SignatoryInfoDataFormType> | null;
  onSignatoryDelete: any;
  onSignatoryFormDataClear: React.MouseEventHandler;
}

const FormContent: FunctionComponent<FormContentProps> = ({
  onFormInitialValuesChange,
  disableAllFields,
  validationSchema,
  onSignatoryDelete,
  onSignatoryFormDataClear,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { values, setFieldValue, submitForm, initialValues, validateForm, resetForm, isValid, setFieldError } =
    useFormikContext<SignatoryInfoDataFormType>();

  const [suggestionsForPlacefBirth, setSuggestionsForPlaceOfBirth] = useState<CitiesAndZipCodesSuggestion[]>([]);
  const [suggestedLocationsLoading, setSuggestedLocationsLoading] = useState(false);
  const [suggestionsForZipCodeOfBirth, setSuggestionsForZipCodeOfBirth] = useState<CitiesAndZipCodesSuggestion[]>([]);
  const [suggestedZipCodesLoading, setSuggestedZipCodesLoading] = useState(false);
  const [showSignatoryModal, setShowSignatoryModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [signatoryToRemove, setSignatoryToRemove] = useState(null);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const quoteSignatoryData = useSelector(quoteSelectors.selectStakeholderContacts).find(
    (contact) => contact.milesStakeholderContactType === 'SIGNATORY'
  );
  const customerSignatoryData = useSelector(customerSelectors.selectStakeholderContacts).SIGNATORY;
  const isQuoteStakeholderAvailable = useMemo(() => !!quoteSignatoryData, [quoteSignatoryData]);
  const isCustomerStakeholderAvailable = useMemo(() => !!customerSignatoryData, [customerSignatoryData]);
  const availableSignatories = useSelector(customerSelectors.selectAvailableSignatories);
  const quoteId = useSelector(selectQuoteIdFromDataStorage);
  const isFormSubmitted = !!useSelector(creditApplicationSelectors.selectSubmittedForms).find(
    (form) => form === CustomerDetailsSection.SIGNATORY_INFO
  );
  const [isStakeholderUnlinked, setIsStakeholderUnlinked] = useState(!isQuoteStakeholderAvailable);
  const [isEditMode, setIsEditMode] = useState(!isFormSubmitted && isStakeholderUnlinked);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const onSearch = () => {
    setShowSignatoryModal(true);
  };

  useEffect(() => {
    if (!quoteSignatoryData) {
      setIsEditMode(true);
      setIsStakeholderUnlinked(true);
    }
  }, [quoteSignatoryData]);

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, validationSchema]);

  const isBornInFrance = values.ceoBirthCountry === CountryCodesEnum.FR;
  const isFrenchTerritory = frenchTerritoriesOptions.some((option) => option.value === values.ceoBirthCountry);
  const areSignatoryFieldsDisabled = disableAllFields || !isEditMode;
  const isContactDataEditable = isEditMode && isCustomerStakeholderAvailable;

  const onSelectSuggestion = (suggestion: string) => {
    const zipMatch = suggestion.match(/\b\d{5}\b/);
    const zip = zipMatch ? zipMatch[0] : null;
    const city = zip ? suggestion.replace(zip, '').trim() : suggestion.trim();

    setIsUserTyping(false);
    if (isFrenchTerritory) {
      setFieldValue('ceoBirthPlaceZip', zip);
    }
    setFieldValue('ceoBirthPlaceCity', formatCityNameLettersAndSpaceOnly(city));
  };

  const onZipCodeOfBirthChange = (value: string) => {
    setIsUserTyping(true);
    setFieldValue('ceoBirthPlaceZip', value);
  };

  const onPlaceOfBirthChange = (value: string) => {
    setIsUserTyping(true);
    setFieldValue('ceoBirthPlaceCity', value);
  };

  const initiateSignatoryRemoval = (signatory) => {
    setSignatoryToRemove(signatory);
    setShowDeletionModal(true);
  };

  const handleSignatoryStakeholderRemoval = async () => {
    await onSignatoryDelete(signatoryToRemove);
    setIsEditMode(true);
    setShowDeletionModal(false);
  };

  const handleEditSignatoryStakeholder = async () => {
    if (!quoteId) return;

    try {
      if (!isStakeholderUnlinked) {
        setIsUnlinking(true);
        dispatch(loadingActions.setLoading(true));
        await unlinkStakeholderContact(
          assertDefined(quoteId),
          assertDefined(quoteSignatoryData?.milesStakeholderContactId)
        );
        setIsStakeholderUnlinked(true);
      }

      setIsEditMode(true);
      dispatch(setFormAsNotSubmitted(CustomerDetailsSection.SIGNATORY_INFO));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsUnlinking(false);
      dispatch(loadingActions.setLoading(false));
    }
  };

  const handleSignatoryInfoSubmit = async () => {
    if (values.email) {
      const tlsValid = await validateEmailTls(values.email, setFieldError, dispatch);

      if (!tlsValid) {
        setIsEditMode(true);
        return;
      }
    }

    await submitForm();

    if (isValid) {
      setIsEditMode(false);
    }
  };

  const handleSearchSignatorySelection = (selected: StakeholderContactDefinition) => {
    dispatch(setStakeholderContact(selected));
    setShowSignatoryModal(false);
    if (isValid) {
      handleSignatoryInfoSubmit();
    }
  };

  useEffect(() => {
    if (!values.ceoBirthPlaceZip || values.ceoBirthPlaceZip?.length < 5 || !isBornInFrance) return;

    async function fetchSuggestions() {
      try {
        setSuggestedZipCodesLoading(true);
        const res = await localitiesWithZipCodes(values.ceoBirthPlaceZip || '');
        setSuggestionsForZipCodeOfBirth(res);
        setSuggestionsForPlaceOfBirth([]);
      } catch {
        setSuggestedZipCodesLoading(false);
      } finally {
        setSuggestedZipCodesLoading(false);
      }
    }

    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.ceoBirthPlaceZip]);

  useEffect(() => {
    if (!values.ceoBirthPlaceCity || values.ceoBirthPlaceCity?.length < 3) return;

    async function fetchSuggestions() {
      try {
        setSuggestedLocationsLoading(true);
        const res = await zipCodesForLocality(values.ceoBirthPlaceCity || '');
        setSuggestionsForPlaceOfBirth(res);
        setSuggestionsForZipCodeOfBirth([]);
      } catch {
        setSuggestedLocationsLoading(false);
      } finally {
        setSuggestedLocationsLoading(false);
      }
    }

    const handler = setTimeout(() => {
      fetchSuggestions();
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(handler);
    };
  }, [values.ceoBirthPlaceCity]);

  return (
    <Fieldset className="u-pt-none">
      <Layout>
        <Layout.Item default="1/1" className="u-text-right u-pb-small">
          <Button
            type="button"
            disabled={!availableSignatories?.length || !isEditMode || !isStakeholderUnlinked}
            className="u-mr"
            ariaLabel="search signatory"
            secondary
            icon="semantic-search"
            onClick={onSearch}
          />
          <Button
            type="button"
            secondary
            disabled={!isStakeholderUnlinked}
            ariaLabel="delete signatory"
            icon="semantic-delete"
            onClick={(e) => {
              resetForm();
              setIsEditMode(true);
              onSignatoryFormDataClear(e);
            }}
          />
          <Button
            type="button"
            secondary
            className="u-ml"
            disabled={isUnlinking || isEditMode}
            onClick={handleEditSignatoryStakeholder}
          >
            {t('step4:stakeholderSection:buttons:edit')}
          </Button>
        </Layout.Item>
      </Layout>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <Gender t={t} isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoFirstName t={t} isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoLastName t={t} isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthName t={t} isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoDateOfBirth
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              label={t('step4:ceoDateOfBirth:label')}
              infoText={t('step4:dateInfoText:label')}
              isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable}
            />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoNationality
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable}
            />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthCountry
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable}
            />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthPlaceCity
              t={t}
              required={isBornInFrance}
              onChange={onPlaceOfBirthChange}
              onSelectSuggestion={onSelectSuggestion}
              isLoading={suggestedLocationsLoading}
              suggestions={isUserTyping ? suggestionsForPlacefBirth : []}
              isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable}
            />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/3" s="1/1">
            <CeoBirthPlaceZip
              t={t}
              isDisabled={areSignatoryFieldsDisabled || isCustomerStakeholderAvailable || !isFrenchTerritory}
              isLoading={suggestedZipCodesLoading}
              suggestions={suggestionsForZipCodeOfBirth}
              onChange={onZipCodeOfBirthChange}
              onSelectSuggestion={onSelectSuggestion}
            />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <CountryCodeMobilePhone
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={areSignatoryFieldsDisabled && !isContactDataEditable}
            />
          </Layout.Item>
          <Layout.Item default="2/5" m="1/2" s="1/1">
            <MobilePhone
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              isDisabled={areSignatoryFieldsDisabled && !isContactDataEditable}
              required={false}
            />
          </Layout.Item>
          <Layout.Item default="2/5" m="1/2" s="1/1">
            <Email t={t} isDisabled={areSignatoryFieldsDisabled && !isContactDataEditable} />
          </Layout.Item>
        </Layout>
        <ButtonContainer right>
          <Layout className="u-mt-small" right>
            <Layout.Item default="1/3" m="2/5" s="2/3">
              <Button full type="submit" onClick={handleSignatoryInfoSubmit} disabled={!isEditMode || isUnlinking}>
                {t('step3:navigation:continue')}
              </Button>
            </Layout.Item>
          </Layout>
        </ButtonContainer>
      </Fieldset.Row>
      <SignatorySelectionModal
        shown={showSignatoryModal}
        onAbort={() => setShowSignatoryModal(false)}
        onConfirm={handleSearchSignatorySelection}
        signatoriesList={availableSignatories}
        onSignatoryDelete={initiateSignatoryRemoval}
      />
      <StakeholderRemovalModal
        isSignatory
        shown={showDeletionModal}
        onAbort={() => setShowDeletionModal(false)}
        onConfirm={handleSignatoryStakeholderRemoval}
      />
    </Fieldset>
  );
};
